import type { AdminCategory, LocalizedPair } from '@diora/contracts';
import { editablePair } from '@diora/contracts';
import type { QueryKey } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Plus, UtensilsCrossed } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/app-shell';
import { BilingualField } from '@/components/bilingual';
import { FormPage, RecordPage, useDirty, useRecord } from '@/components/form-page';
import { ImagePicker } from '@/components/image-picker';
import { PublishBlockers, PublishWarnings, RowActions, StatusBadge, TranslationBadge } from '@/components/records';
import {
  Alert,
  Button,
  DataTable,
  EmptyState,
  Modal,
  Pagination,
  SkeletonRows,
  Toolbar,
} from '@/components/ui';
import {
  apiPatch,
  apiPost,
  useAdminList,
  useAdminMutation,
  useLifecycle,
  useListParams,
  useReorder,
} from '@/hooks/admin';
import { ApiError, countsFromError } from '@/lib/errors';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { ListFilters } from './_filters';

const URL = '/admin/menu/categories';
/** The list this screen's form pages return to. */
const LIST = '/menu/categories';
const INVALIDATE: QueryKey[] = [['categories'], ['category'], qk.dashboard];

export function CategoriesPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const [params, setParams] = useListParams();
  const key = qk.categories(params);
  const list = useAdminList<AdminCategory>(key, URL, params);
  const invalidate = INVALIDATE;

  const [archiveBlocked, setArchiveBlocked] = useState<{ row: AdminCategory; itemCount: number } | null>(null);

  const lifecycle = useLifecycle(URL, invalidate);
  const reorder = useReorder(URL, invalidate);

  /**
   * Archiving a category hides every dish inside it. The server refuses the
   * first attempt with the count, and only accepts it once the caller has
   * confirmed they saw that number.
   */
  const archive = useAdminMutation(
    ({ row, confirm }: { row: AdminCategory; confirm: boolean }) =>
      apiPost(`${URL}/${row.id}/archive`, {
        expectedVersion: row.version,
        confirmDependents: confirm,
      }),
    {
      successMessage: t('publish.archived'),
      invalidate,
      onSuccess: () => setArchiveBlocked(null),
      onError: (error, variables) => {
        if (error instanceof ApiError && error.code === 'HAS_DEPENDENTS') {
          const counts = countsFromError(error, 'confirmDependents');
          setArchiveBlocked({
            row: variables.row,
            itemCount: counts.ITEMS ?? counts.AFFECTS_ITEMS ?? variables.row.itemCount,
          });
          return true;
        }
      },
    },
  );

  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  const onLastPage = page * pageSize >= list.total;

  function reorderBy(id: string, delta: number) {
    if (!list.contentRevision) return;
    reorder.mutate({
      id,
      delta,
      visible: list.rows.map((row) => row.id),
      contentRevision: list.contentRevision,
    });
  }

  return (
    <>
      <PageHeader
        title={t('categories.title')}
        description={t('categories.subtitle')}
        actions={
          canEdit && (
            <Link to={`${LIST}/new`} className="dio-btn dio-btn--primary">
              <Plus className="dio-icon" aria-hidden="true" />
              {t('categories.newTitle')}
            </Link>
          )
        }
      />

      <Toolbar>
        <ListFilters params={params} onChange={setParams} />
      </Toolbar>

      {list.isLoading ? (
        <SkeletonRows />
      ) : list.rows.length === 0 ? (
        <EmptyState
          icon={<UtensilsCrossed aria-hidden="true" />}
          title={params.search ? t('common.noResults') : t('categories.empty')}
          hint={params.search ? t('common.noResultsHint') : t('categories.emptyHint')}
          action={
            canEdit && !params.search ? (
              <Link to={`${LIST}/new`} className="dio-btn dio-btn--primary">
                {t('categories.newTitle')}
              </Link>
            ) : undefined
          }
        />
      ) : (
        <DataTable
          caption={t('categories.title')}
          columns={[
            <span className="sr-only">{t('common.image')}</span>,
            t('categories.name'),
            t('categories.itemCount'),
            t('common.status'),
            <span className="sr-only">{t('common.actions')}</span>,
          ]}
        >
          {list.rows.map((row, index) => (
            <tr key={row.id}>
              <td style={{ inlineSize: '1%' }}>
                {row.image ? (
                  <img src={row.image.thumbUrl} alt="" className="dio-table__thumb" />
                ) : (
                  <span className="dio-table__thumb" aria-hidden="true" />
                )}
              </td>

              <td>
                {/* Each name is set in its own script and direction — an Arabic
                    name inside an English interface still reads right-to-left. */}
                <div className="dio-table__name flex flex-wrap items-center gap-2">
                  <span lang="ar" dir="rtl">
                    {row.name.ar || '—'}
                  </span>
                  <span className="dio-meta" aria-hidden="true">
                    /
                  </span>
                  <span lang="en" dir="ltr">
                    {row.name.en || '—'}
                  </span>
                </div>
                <TranslationBadge translation={row.translation} />
              </td>

              <td className="dio-table__num">{row.itemCount}</td>

              <td>
                <StatusBadge status={row.publicationStatus} />
              </td>

              <td className="dio-table__actions">
                <div className="dio-actions">
                  {/* Reorder stays outside the menu: it is the one action taken
                      repeatedly, several rows in a row, and burying it two
                      clicks deep would make ordering a menu of twenty-two
                      categories unbearable. */}
                  {canEdit && (
                    <>
                      {/* An archived category holds no place in the order the
                          server keeps, so it cannot be moved within it. */}
                      <Button
                        size="sm"
                        iconOnly
                        variant="ghost"
                        aria-label={t('common.moveUp')}
                        disabled={
                          row.publicationStatus === 'ARCHIVED' ||
                          reorder.isPending ||
                          (index === 0 && page === 1)
                        }
                        onClick={() => reorderBy(row.id, -1)}
                      >
                        <ChevronUp className="dio-icon" aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm"
                        iconOnly
                        variant="ghost"
                        aria-label={t('common.moveDown')}
                        disabled={
                          row.publicationStatus === 'ARCHIVED' ||
                          reorder.isPending ||
                          (index === list.rows.length - 1 && onLastPage)
                        }
                        onClick={() => reorderBy(row.id, 1)}
                      >
                        <ChevronDown className="dio-icon" aria-hidden="true" />
                      </Button>
                    </>
                  )}
                  <Link to={`${LIST}/${row.id}`} className="dio-btn dio-btn--secondary dio-btn--sm">
                    {canEdit ? t('common.edit') : t('common.details')}
                  </Link>
                  <RowActions
                    status={row.publicationStatus}
                    translation={row.translation}
                    canEdit={canEdit}
                    busy={lifecycle.busy || archive.isPending}
                    label={t('common.openMenuFor', {
                      name: row.name.ar || row.name.en,
                    })}
                    handlers={{
                      onPublish: () => lifecycle.publish.mutate({ id: row.id, version: row.version }),
                      onUnpublish: () => lifecycle.unpublish.mutate({ id: row.id, version: row.version }),
                      onArchive: () => archive.mutate({ row, confirm: false }),
                      onRestore: () => lifecycle.restore.mutate({ id: row.id, version: row.version }),
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <Pagination
        page={page}
        pageSize={pageSize}
        total={list.total}
        onPage={(next) => setParams({ page: next })}
      />

      <Modal
        open={archiveBlocked !== null}
        onClose={() => setArchiveBlocked(null)}
        title={t('publish.confirmArchiveTitle')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setArchiveBlocked(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={archive.isPending}
              onClick={() =>
                archiveBlocked && archive.mutate({ row: archiveBlocked.row, confirm: true })
              }
            >
              {t('categories.archiveConfirm')}
            </Button>
          </div>
        }
      >
        <Alert tone="warning">
          {t('categories.archiveHasItems', { count: archiveBlocked?.itemCount ?? 0 })}
        </Alert>
      </Modal>
    </>
  );
}

/**
 * Create, and edit, at their own addresses.
 *
 * One component serves both: `/menu/categories/new` mounts the form with no
 * record, `/menu/categories/<id>` fetches the record first and mounts it with
 * one. Splitting them would mean two copies of a form whose only difference is
 * which verb it ends in.
 */
export function CategoryFormPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const record = useRecord<AdminCategory>(qk.category(id ?? ''), URL, id);

  if (!id) return <CategoryForm category={null} canEdit={canEdit} />;

  return (
    <RecordPage title={t('categories.editTitle')} backTo={LIST} record={record}>
      {(row) => <CategoryForm key={row.id} category={row} canEdit={canEdit} />}
    </RecordPage>
  );
}

function CategoryForm({ category, canEdit }: { category: AdminCategory | null; canEdit: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [name, setName] = useState<LocalizedPair>(category?.name ?? { ar: '', en: '' });
  const [description, setDescription] = useState<LocalizedPair>(editablePair(category?.description));
  const [imageId, setImageId] = useState<string | null>(category?.image?.id ?? null);
  const [image, setImage] = useState(category?.image ?? null);

  const dirty = useDirty({ name, description, imageId });

  const save = useAdminMutation(
    () => {
      const body = {
        nameAr: name.ar,
        nameEn: name.en,
        descriptionAr: description.ar,
        descriptionEn: description.en,
        imageId,
      };
      return category
        ? apiPatch(`${URL}/${category.id}`, { ...body, expectedVersion: category.version })
        : apiPost(URL, body);
    },
    {
      successMessage: category ? t('common.saved') : t('common.created'),
      invalidate: INVALIDATE,
      onSuccess: () => navigate(LIST),
    },
  );

  return (
    <FormPage
      title={category ? t('categories.editTitle') : t('categories.newTitle')}
      description={t('categories.subtitle')}
      backTo={LIST}
      dirty={dirty}
      saving={save.isPending}
      canSave={name.ar.trim().length > 0}
      onSave={canEdit ? () => save.mutate(undefined) : undefined}
    >
      {category && <PublishBlockers translation={category.translation} />}
      {category && <PublishWarnings translation={category.translation} />}

      <BilingualField
        label={t('categories.name')}
        value={name}
        onChange={setName}
        required
        maxLength={100}
        disabled={!canEdit}
      />

      <BilingualField
        label={t('categories.description')}
        value={description}
        onChange={setDescription}
        multiline
        maxLength={1000}
        disabled={!canEdit}
      />

      <ImagePicker
        label={t('categories.image')}
        hint={t('categories.imageHint')}
        surface="category"
        value={image}
        disabled={!canEdit}
        onChange={(picked) => {
          setImageId(picked?.id ?? null);
          setImage(picked ? { ...picked, storageKey: '', alt: { ar: '', en: '' } } : null);
        }}
      />

      {!category && <p className="dio-hint">{t('categories.createHint')}</p>}
    </FormPage>
  );
}
